import { createSlice, createAsyncThunk, createEntityAdapter, type EntityState } from '@reduxjs/toolkit'

import { Challenge, listChallengesForUser } from '@api/orval/apiHooks'
import { QueryParam } from '@components/Filters/filter.types'

type ArgsData = { userId: number; params?: QueryParam }
type RejectData = { rejectValue: string }

// TODO(nikita): remove challenges slice
const challengesAdapter = createEntityAdapter({
  selectId: (challenge: Challenge) => challenge.id,
  sortComparer: (a, b) => b.id - a.id,
})

const fetchChallengesThunk = createAsyncThunk<Challenge[], ArgsData, RejectData>(
  'challenges/fetchAll',
  async ({ userId, params }, { rejectWithValue }) => {
    try {
      const response = await listChallengesForUser(userId, params)

      return response?.collection || []
    } catch (err) {
      console.error('Failed to fetch challenges:', err)

      return rejectWithValue('Failed to fetch challenges')
    }
  },
)

interface ChallengesState extends EntityState<Challenge, number> {
  loading: boolean
  error: string | null
}

const initialState: ChallengesState = challengesAdapter.getInitialState({
  loading: false,
  error: null,
})

const challengesSlice = createSlice({
  name: 'challenges',
  initialState,
  reducers: {
    updateChallengePartial: (state, action) => {
      const { id, changes } = action.payload
      const existing = state.entities[id]
      const { newTradingAccount, updatedTradingAccount, ...rest } = changes

      if (existing) {
        if (newTradingAccount) {
          existing.trading_accounts = [changes.newTradingAccount, ...(existing.trading_accounts || [])]
        }

        if (updatedTradingAccount) {
          const updated = changes.updatedTradingAccount
          const accounts = existing.trading_accounts || []

          const index = accounts.findIndex((acc) => acc.id === updated.id)

          if (index !== -1) {
            accounts[index] = updated
          } else {
            accounts.unshift(updated)
          }

          existing.trading_accounts = accounts
        }

        Object.assign(existing, rest)
      }
    },
    addOneChallenge: challengesAdapter.upsertOne,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChallengesThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchChallengesThunk.fulfilled, (state, { payload }) => {
        state.loading = false
        if (payload) {
          challengesAdapter.setAll(state, payload)
        } else {
          challengesAdapter.setAll(state, [])
        }
      })
      .addCase(fetchChallengesThunk.rejected, (state, { payload }) => {
        state.loading = false
        state.error = payload ?? 'Unknown error'
      })
  },
})

export const { updateChallengePartial, addOneChallenge } = challengesSlice.actions

export const challengesReducer = challengesSlice.reducer
